import React, { useState, useEffect } from 'react';

import axios from 'axios';
import { useDispatch } from 'react-redux';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

import { getBusinessGroup } from '@/actions/businessGroup';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { usStates } from '../utils/address/usStates';
import { caProvinces } from '../utils/address/caProvinces';
import {
	getPlaceDetails,
	parseGoogleAddress,
	stripWebsiteTracking,
} from '../utils/address/googlePlaces';
import formatPhoneForUser from '../../utils/formatPhoneForUser';
import GoogleBusinessSearch from './GoogleBusinessSearch';

const GOOGLE_STATUS_TEXT = {
	idle: "We'll fill in the details below. You can still edit them.",
	filled: 'Details filled in from Google. Check them below.',
	outside:
		'That business is outside the US and Canada, so enter the address yourself.',
	failed: "Couldn't load details from Google. Enter them below.",
};

const GENERIC_ERROR =
	'There was an error creating your new location. Please contact support@chekkit.io for help.';

const COUNTRIES = {
	US: {
		label: 'United States',
		regionKey: 'state',
		regionLabel: 'State',
		regions: usStates,
		postalKey: 'zipCode',
		postalLabel: 'ZIP code',
	},
	CA: {
		label: 'Canada',
		regionKey: 'province',
		regionLabel: 'Province',
		regions: caProvinces,
		postalKey: 'postalCode',
		postalLabel: 'Postal code',
	},
};

const emptyForm = {
	companyName: '',
	legalBusinessName: '',
	phone: '',
	website: '',
	country: 'US',
	street: '',
	city: '',
	state: '',
	province: '',
	zipCode: '',
	postalCode: '',
};

const getMissingFields = (form) => {
	const { regionKey, postalKey } = COUNTRIES[form.country];
	const required = {
		companyName: 'Enter the name customers know this location by',
		legalBusinessName: 'Enter the legal business name',
		website: 'Enter the website for this location',
		street: 'Enter the street address',
		city: 'Enter the city',
		[regionKey]: `Choose a ${COUNTRIES[form.country].regionLabel.toLowerCase()}`,
		[postalKey]: `Enter the ${COUNTRIES[form.country].postalLabel}`,
	};

	return Object.fromEntries(
		Object.entries(required).filter(([key]) => !form[key]?.trim()),
	);
};

const Field = ({ id, label, error, optional, className, children }) => (
	<div className={cn('grid gap-1.5', className)}>
		<Label htmlFor={id}>
			{label}
			{optional && (
				<span className="ml-1 font-normal text-muted-foreground">
					(optional)
				</span>
			)}
		</Label>
		{children}
		{error && (
			<p id={`${id}-error`} className="text-sm text-destructive">
				{error}
			</p>
		)}
	</div>
);

const TextField = ({
	id,
	label,
	error,
	optional,
	className,
	...inputProps
}) => (
	<Field
		id={id}
		label={label}
		error={error}
		optional={optional}
		className={className}
	>
		<Input
			id={id}
			aria-invalid={Boolean(error)}
			aria-describedby={error ? `${id}-error` : undefined}
			className={cn(error && 'border-destructive')}
			{...inputProps}
		/>
	</Field>
);

const AddNewLocationDialog = ({
	handleClose,
	isOpen,
	handleSwitchLocation,
}) => {
	const dispatch = useDispatch();
	const [price, setPrice] = useState(null);
	const [priceLoading, setPriceLoading] = useState(true);
	const [form, setForm] = useState(emptyForm);
	const [fieldErrors, setFieldErrors] = useState({});
	const [errorText, setErrorText] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [newBusinessId, setNewBusinessId] = useState('');
	const [googleStatus, setGoogleStatus] = useState('idle');

	useEffect(() => {
		if (!isOpen) return;

		const fetchPrice = async () => {
			setPriceLoading(true);
			try {
				const { data } = await axios.get('/api/current-monthly-price');
				const monthlyPrice = Number(data?.totalMonthlyPrice);
				setPrice(
					!data?.error && Number.isFinite(monthlyPrice)
						? monthlyPrice.toFixed(2)
						: null,
				);
			} catch (error) {
				setPrice(null);
			} finally {
				setPriceLoading(false);
			}
		};

		fetchPrice();
	}, [isOpen]);

	const country = COUNTRIES[form.country];

	const updateField = (name, value) => {
		setForm((prev) => ({ ...prev, [name]: value }));
		setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
	};

	const handleCountryChange = (value) => {
		setForm((prev) => ({ ...prev, country: value, state: '', province: '' }));
		setFieldErrors({});
	};

	const handleGooglePlaceSelect = async (placeId) => {
		try {
			const place = await getPlaceDetails(placeId, [
				'name',
				'formatted_phone_number',
				'website',
				'address_components',
			]);
			const address = parseGoogleAddress(place.address_components);
			const isSupportedCountry = Boolean(COUNTRIES[address.country]);

			setForm((prev) => ({
				...prev,
				companyName: place.name || prev.companyName,
				legalBusinessName: prev.legalBusinessName || place.name || '',
				phone: place.formatted_phone_number
					? formatPhoneForUser(place.formatted_phone_number)
					: prev.phone,
				website: stripWebsiteTracking(place.website) || prev.website,
				...(isSupportedCountry && {
					street: '',
					city: '',
					state: '',
					province: '',
					zipCode: '',
					postalCode: '',
					...address,
				}),
			}));
			setFieldErrors({});
			setGoogleStatus(isSupportedCountry ? 'filled' : 'outside');
		} catch (error) {
			setGoogleStatus('failed');
		}
	};

	const handleAddNewLocation = async (e) => {
		e.preventDefault();
		setErrorText('');

		const missing = getMissingFields(form);
		setFieldErrors(missing);
		if (Object.keys(missing).length) return;

		setSubmitting(true);

		try {
			const res = await axios.post('/api/add-new-location', {
				friendlyBusinessName: form.companyName,
				legalBusinessName: form.legalBusinessName,
				phone: form.phone,
				website: form.website,
				businessAddress: {
					country: form.country,
					street: form.street,
					city: form.city,
					[country.regionKey]: form[country.regionKey],
					[country.postalKey]: form[country.postalKey],
				},
			});

			if (res.data && !res.data.error) {
				setNewBusinessId(res.data.newBusinessId);
				return;
			}

			setErrorText(res?.data?.message || GENERIC_ERROR);
		} catch (error) {
			setErrorText(
				error?.response?.data?.message ||
					error?.response?.data?.error ||
					GENERIC_ERROR,
			);
		} finally {
			setSubmitting(false);
		}
	};

	const handleOpenChange = (open) => {
		if (open || submitting) return;
		if (newBusinessId) dispatch(getBusinessGroup());
		setForm(emptyForm);
		setFieldErrors({});
		setErrorText('');
		setNewBusinessId('');
		setGoogleStatus('idle');
		handleClose();
	};

	if (newBusinessId) {
		return (
			<Dialog open={isOpen} onOpenChange={handleOpenChange}>
				<DialogContent className="max-w-md">
					<DialogHeader className="items-center text-center sm:text-center">
						<CheckCircle2
							className="mb-2 size-10 text-green-600"
							aria-hidden="true"
						/>
						<DialogTitle>Location added</DialogTitle>
						<DialogDescription>
							{`${form.companyName} is ready. Switch to it to finish setting it up.`}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							className="w-full"
							onClick={() => handleSwitchLocation({ _id: newBusinessId }, true)}
						>
							Go to new location
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent
				className="flex max-h-[90dvh] max-w-xl flex-col gap-0 p-0"
				onEscapeKeyDown={(e) => {
					// Escape should only close the open Google suggestions, not the whole form.
					if (
						document.activeElement?.getAttribute('aria-expanded') === 'true'
					) {
						e.preventDefault();
					}
				}}
			>
				<DialogHeader className="border-b px-6 pb-3 pt-5 pr-12">
					<DialogTitle>Add a location</DialogTitle>
					{priceLoading ? (
						<Skeleton className="h-5 w-full max-w-sm" />
					) : (
						<DialogDescription>
							{price
								? `Same plan and features as this location, billed at $${price}/month.`
								: 'Same plan and features as this location, billed as its own monthly subscription.'}
						</DialogDescription>
					)}
				</DialogHeader>

				<form
					id="add-location-form"
					noValidate
					autoComplete="off"
					onSubmit={handleAddNewLocation}
					className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-4"
				>
					{window.google?.maps?.places && (
						<div className="grid gap-1.5 rounded-lg border bg-muted/40 p-3">
							<Label htmlFor="location-google-search">
								Find your business on Google
							</Label>
							<GoogleBusinessSearch
								id="location-google-search"
								countries={['us', 'ca']}
								onSelect={handleGooglePlaceSelect}
								disabled={submitting}
							/>
							<p
								className={cn(
									'flex items-center gap-1.5 text-sm text-muted-foreground',
									googleStatus === 'idle' && 'sr-only',
									googleStatus === 'filled' &&
										'text-green-700 dark:text-green-500',
								)}
								aria-live="polite"
							>
								{googleStatus === 'filled' && (
									<CheckCircle2
										className="size-4 shrink-0"
										aria-hidden="true"
									/>
								)}
								{GOOGLE_STATUS_TEXT[googleStatus]}
							</p>
						</div>
					)}

					<fieldset className="grid gap-3" disabled={submitting}>
						<legend className="mb-2 text-base font-semibold">
							Business details
						</legend>
						<div className="grid gap-3 sm:grid-cols-2">
							<TextField
								id="location-company-name"
								label="Location name"
								placeholder="Your Business – Downtown"
								value={form.companyName}
								onChange={(e) => updateField('companyName', e.target.value)}
								error={fieldErrors.companyName}
							/>
							<TextField
								id="location-legal-name"
								label="Legal business name"
								placeholder="Your Business Inc."
								value={form.legalBusinessName}
								onChange={(e) =>
									updateField('legalBusinessName', e.target.value)
								}
								error={fieldErrors.legalBusinessName}
							/>
						</div>
						<div className="grid gap-3 sm:grid-cols-2">
							<TextField
								id="location-phone"
								label="Business phone"
								type="tel"
								placeholder="(555) 555-5555"
								optional
								value={form.phone}
								onChange={(e) =>
									updateField('phone', formatPhoneForUser(e.target.value))
								}
							/>
							<TextField
								id="location-website"
								label="Website"
								placeholder="yourbusiness.com"
								value={form.website}
								onChange={(e) => updateField('website', e.target.value)}
								error={fieldErrors.website}
							/>
						</div>
					</fieldset>

					<fieldset className="grid gap-3" disabled={submitting}>
						<legend className="mb-2 text-base font-semibold">Address</legend>
						<div className="grid gap-3 sm:grid-cols-[10rem_1fr]">
							<Field id="location-country" label="Country">
								<Select
									value={form.country}
									onValueChange={handleCountryChange}
								>
									<SelectTrigger id="location-country" className="w-full">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{Object.entries(COUNTRIES).map(([code, { label }]) => (
											<SelectItem key={code} value={code}>
												{label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</Field>
							<TextField
								id="location-street"
								label="Street address"
								placeholder="123 Main Street"
								value={form.street}
								onChange={(e) => updateField('street', e.target.value)}
								error={fieldErrors.street}
							/>
						</div>
						<div className="grid gap-3 sm:grid-cols-3">
							<TextField
								id="location-city"
								label="City"
								value={form.city}
								onChange={(e) => updateField('city', e.target.value)}
								error={fieldErrors.city}
							/>
							<Field
								id="location-region"
								label={country.regionLabel}
								error={fieldErrors[country.regionKey]}
							>
								<Select
									value={form[country.regionKey] || undefined}
									onValueChange={(val) => updateField(country.regionKey, val)}
								>
									<SelectTrigger
										id="location-region"
										aria-invalid={Boolean(fieldErrors[country.regionKey])}
										className={cn(
											'w-full',
											fieldErrors[country.regionKey] && 'border-destructive',
										)}
									>
										<SelectValue placeholder="Select" />
									</SelectTrigger>
									<SelectContent>
										{country.regions.map((region) => (
											<SelectItem key={region} value={region}>
												{region}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</Field>
							<TextField
								id="location-postal"
								label={country.postalLabel}
								value={form[country.postalKey]}
								onChange={(e) => updateField(country.postalKey, e.target.value)}
								error={fieldErrors[country.postalKey]}
							/>
						</div>
					</fieldset>

					{errorText && (
						<Alert variant="destructive">
							<AlertCircle className="size-4" />
							<AlertDescription>{errorText}</AlertDescription>
						</Alert>
					)}
				</form>

				<DialogFooter className="gap-2 border-t px-6 py-3">
					<Button
						type="button"
						variant="ghost"
						onClick={() => handleOpenChange(false)}
						disabled={submitting}
					>
						Cancel
					</Button>
					<Button type="submit" form="add-location-form" disabled={submitting}>
						{submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
						{submitting ? 'Adding location…' : 'Add location'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default AddNewLocationDialog;
